import { useCallback, useEffect, useRef, useState } from 'react';
import { useUser } from '@clerk/react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { advisorAccountActivationApi, advisorAccountActivationKeys } from '@client/api/waterlily';
import { setAuthToken } from '@client/api/queryClient';
import { setRoleOverride } from '@client/hooks/useRoleSwitch';
import { useClerkClientAuth } from '@client/hooks/useClerkClientAuth';
import type { API } from '@client/api/types';
import {
  getPhoneVerificationErrorMessage,
  useClerkPhoneVerification,
} from '@client/hooks/useClerkPhoneVerification';

export type AdvisorAccountActivationStep =
  | 'loading'
  | 'confirmation'
  | 'phone'
  | 'verify'
  | 'provisioning'
  | 'blocked'
  | 'error';

export function useAdvisorAccountActivation({
  startAfterConfirmation = false,
  initialPreflight = null,
  emailOnlyOverride = false,
}: {
  startAfterConfirmation?: boolean;
  initialPreflight?: API.AdvisorAccountActivationPreflight | null;
  emailOnlyOverride?: boolean;
} = {}) {
  const { user, isLoaded } = useUser();
  const clerkUserId = user?.id;
  const { getClerkToken } = useClerkClientAuth();
  const [step, setStep] = useState<AdvisorAccountActivationStep>(() =>
    initialPreflight
      ? initialPreflight.status === 'blocked'
        ? 'blocked'
        : 'confirmation'
      : 'loading',
  );
  const [preflight, setPreflight] = useState<API.AdvisorAccountActivationPreflight | null>(
    initialPreflight,
  );
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activationSubmitted, setActivationSubmitted] = useState(false);
  const [failedOperation, setFailedOperation] = useState<'preflight' | 'activation'>('preflight');
  const hasStartedAfterConfirmationRef = useRef(false);
  const overrideConnection = emailOnlyOverride ? ('email' as const) : undefined;

  const preflightQuery = useQuery({
    queryKey: advisorAccountActivationKeys.preflight(overrideConnection),
    queryFn: () => advisorAccountActivationApi.getPreflight({ overrideConnection }),
    enabled: !initialPreflight && isLoaded && !!clerkUserId,
    retry: false,
  });
  const activationMutation = useMutation({
    mutationFn: () => advisorAccountActivationApi.activate({ overrideConnection }),
  });
  const { refetch: refetchPreflight } = preflightQuery;
  const { mutateAsync: activateAccount } = activationMutation;

  const applyPreflight = useCallback(
    (result: API.AdvisorAccountActivationPreflight) => {
      setPreflight(result);

      if (result.status === 'already-advisor' && result.advisorId) {
        if (!clerkUserId) {
          setError('Your signed-in account is not available. Please refresh the page.');
          setFailedOperation('preflight');
          setStep('error');
          return;
        }

        setRoleOverride('advisor', clerkUserId);
        globalThis.location.assign(`/advisors/${result.advisorId}`);
        return;
      }

      setStep(result.status === 'blocked' ? 'blocked' : 'confirmation');
    },
    [clerkUserId],
  );

  useEffect(() => {
    if (initialPreflight || !preflightQuery.isSuccess) return;
    applyPreflight(preflightQuery.data);
  }, [applyPreflight, initialPreflight, preflightQuery.data, preflightQuery.isSuccess]);

  useEffect(() => {
    if (initialPreflight || !preflightQuery.isError) return;
    setError(getPhoneVerificationErrorMessage(preflightQuery.error));
    setFailedOperation('preflight');
    setStep('error');
  }, [initialPreflight, preflightQuery.error, preflightQuery.isError]);

  const loadPreflight = useCallback(async () => {
    if (!isLoaded || !clerkUserId) return;

    setStep('loading');
    setError(null);
    const result = await refetchPreflight();
    if (result.data) {
      applyPreflight(result.data);
      return;
    }

    if (result.error) {
      setError(getPhoneVerificationErrorMessage(result.error));
      setFailedOperation('preflight');
      setStep('error');
    }
  }, [applyPreflight, clerkUserId, isLoaded, refetchPreflight]);

  const activate = useCallback(async () => {
    if (!user) return;

    setStep('provisioning');
    setIsSubmitting(true);
    setError(null);
    setActivationSubmitted(true);
    try {
      const result = await activateAccount();
      if (!result.requiresTokenRefresh) {
        throw new Error('Your advisor session could not be refreshed. Please try again.');
      }

      await user.reload();
      const freshToken = await getClerkToken({ skipCache: true });
      if (!freshToken) {
        throw new Error('Your advisor session could not be refreshed. Please try again.');
      }

      setAuthToken(freshToken);
      setRoleOverride('advisor', user.id);
      globalThis.location.assign(result.onboardingPath);
    } catch (requestError) {
      setError(getPhoneVerificationErrorMessage(requestError));
      setFailedOperation('activation');
      setStep('error');
    } finally {
      setIsSubmitting(false);
    }
  }, [activateAccount, getClerkToken, user]);

  const activateAfterPhoneVerification = useCallback(async () => activate(), [activate]);

  const phoneVerification = useClerkPhoneVerification({
    onVerified: activateAfterPhoneVerification,
    onStepChange: setStep,
  });
  const { returnToPhone } = phoneVerification;

  const continueFromConfirmation = useCallback(async () => {
    if (!preflight) return;
    if (preflight.phoneVerificationRequired) {
      setError(null);
      returnToPhone();
      return;
    }
    await activate();
  }, [activate, preflight, returnToPhone]);

  useEffect(() => {
    if (
      !startAfterConfirmation ||
      step !== 'confirmation' ||
      hasStartedAfterConfirmationRef.current
    ) {
      return;
    }

    hasStartedAfterConfirmationRef.current = true;
    void continueFromConfirmation();
  }, [continueFromConfirmation, startAfterConfirmation, step]);

  const retryFailedStep = useCallback(async () => {
    if (failedOperation === 'activation') {
      await activate();
      return;
    }
    await loadPreflight();
  }, [activate, failedOperation, loadPreflight]);

  return {
    ...phoneVerification,
    step,
    preflight,
    error: phoneVerification.error ?? error,
    isSubmitting: phoneVerification.isSubmitting || isSubmitting,
    activationSubmitted,
    retryFailedStep,
    continueFromConfirmation,
  };
}
